import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, vModelDynamic as _vModelDynamic, mergeProps as _mergeProps, withDirectives as _withDirectives, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = ["id", "name", "type", "max", "min", "required", "disabled", "aria-invalid", "aria-describedby", "aria-label"]
const _hoisted_3 = ["id"]

import {convertToUTC} from "@/utils";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import {computed, nextTick, onMounted, ref, useAttrs, watch} from "vue";

export default /*@__PURE__*/_defineComponent({
  ...{
    inheritAttrs: false,
},
  __name: 'DatePicker',
  props: /*@__PURE__*/_mergeModels({
    error: {
        type: String,
        default: null,
    },
    errorId: {
        type: String,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    name: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    ariaLabel: {
        type: String,
        default: null,
    },
    ariaDescribedby: {
        type: String,
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
    monthPicker: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    autoApply: {
        type: Boolean,
        default: true,
    },
    enableTimePicker: {
        type: Boolean,
        default: false,
    },
    enableMinutes: {
        type: Boolean,
        default: true,
    },
    enableSeconds: {
        type: Boolean,
        default: false,
    },
    maxDate: {
        type: Date,
        default: undefined,
    },
    autoPosition: {
        type: Boolean,
        default: true,
    },
    minDate: {
        type: Date,
        default: undefined,
    },
    useNative: {
        type: Boolean,
        default: true,
    },
}, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["update:modelValue"], ["update:modelValue"]),
  setup(__props, { emit: __emit }) {

const attrs = useAttrs();


const props = __props;

const type = computed(() => {
    if (props.enableTimePicker) {
        return "datetime-local";
    }
    if (props.monthPicker) {
        return "month";
    }
    return "date";
});

const emit = __emit;
const model = _useModel<Date>(__props, "modelValue");
const datepickerWrapper = ref<HTMLElement | null>(null);

const isDate = (val: unknown): val is Date => {
    return val instanceof Date && !isNaN(val.getTime());
};

function parseDateValue(input: Date | string | undefined): Date | null {
    if (!input) {
        return null;
    }

    if (isDate(input)) {
        return input;
    }

    const value = String(input).trim();
    if (!value) {
        return null;
    }

    const [datePart, timePart] = value.split(" ");
    const dateParts = datePart.split("-").map(Number);

    if (dateParts.length === 3 && dateParts.every((part) => !isNaN(part))) {
        const [year, month, day] = dateParts;
        if (timePart) {
            const [hours = "00", minutes = "00", seconds = "00"] = timePart.split(":");
            const date = new Date(year, month - 1, day, Number(hours), Number(minutes), Number(seconds));
            if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day
                || date.getHours() !== Number(hours) || date.getMinutes() !== Number(minutes) || date.getSeconds() !== Number(seconds)) {
                return null;
            }
            return date;
        }
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return null;
        }
        return date;
    }

    if (dateParts.length === 2 && dateParts.every((part) => !isNaN(part))) {
        const [year, month] = dateParts;
        const date = new Date(year, month - 1, 1);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1) {
            return null;
        }
        return date;
    }

    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatDate(input: Date | string | undefined) {
    const date = parseDateValue(input);
    if (!date) {
        return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    if (props.monthPicker) {
        return `${year}-${month}`;
    }

    if (props.enableTimePicker) {
        const HH = String(date.getHours()).padStart(2, "0");
        const mm = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day} ${HH}:${mm}`;
    }

    return `${year}-${month}-${day}`;
}
const rawValue = ref(formatDate(model.value));

watch(() => model.value, (newValue) => {
    const formatted = formatDate(newValue);
    if (formatted !== formatDate(rawValue.value)) {
        rawValue.value = formatted;
    }
});

function onNativeChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (!parseDateValue(value)) {
        // Invalid date: leave the input as-is so the user can fix it, don't touch the model
        emit("update:modelValue", null);
        return;
    }

    // When the user picks/enters a valid date, emit it back to the parent
    const result = props.monthPicker ? convertToUTC(new Date(`${value}-01`)) : value;
    emit("update:modelValue", result);
}

const dateFormat = computed(() => {
    let format = "yyyy-MM-dd";

    if (props.monthPicker) {
        format = "MM-yyyy";
    }

    if (props.enableTimePicker) {
        if (props.enableSeconds) {
            format = "yyyy-MM-dd HH:mm:ss";
        } else {
            format = "yyyy-MM-dd HH:mm";
        }
    }
    return format;
});

const inputId = computed(() => props.id || props.name || "date-picker");
const inputName = computed(() => props.name || props.id || undefined);
const ariaLabel = computed(() => props.ariaLabel || props.label || undefined);
const describedBy = computed(() => {
    const ids = [props.ariaDescribedby, props.error ? props.errorId : null].filter(Boolean) as string[];
    return ids.length ? ids.join(" ") : undefined;
});

function setDatepickerInputAttributes() {
    if (props.useNative || !datepickerWrapper.value) {
        return;
    }

    const input = datepickerWrapper.value.querySelector<HTMLInputElement>("input");
    if (!input) {
        return;
    }

    input.id = inputId.value;
    input.name = inputName.value || "";
    input.setAttribute("aria-label", ariaLabel.value || "");
    input.setAttribute("aria-invalid", !!props.error ? "true" : "false");
    input.setAttribute("aria-describedby", describedBy.value || "");
    if (props.required) {
        input.setAttribute("required", "required");
    } else {
        input.removeAttribute("required");
    }

    if (props.disabled) {
        input.setAttribute("disabled", "disabled");
    } else {
        input.removeAttribute("disabled");
    }
}

onMounted(() => {
    nextTick(() => setDatepickerInputAttributes());
});

watch(
    [() => props.error, () => props.disabled, () => props.required, () => props.ariaLabel, () => props.label, () => props.ariaDescribedby, () => props.useNative],
    () => {
        nextTick(() => setDatepickerInputAttributes());
    }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: inputId.value,
          class: "form-label"
        }, _toDisplayString(props.label), 9, _hoisted_1))
      : _createCommentVNode("", true),
    (__props.useNative)
      ? _withDirectives((_openBlock(), _createElementBlock("input", _mergeProps({ key: 1 }, _unref(attrs), {
          id: inputId.value,
          name: inputName.value,
          type: type.value,
          class: ["form-control", {'is-invalid': !!__props.error}],
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((rawValue).value = $event)),
          onChange: onNativeChange,
          max: formatDate(props.maxDate),
          min: formatDate(props.minDate),
          required: props.required,
          disabled: props.disabled,
          "aria-invalid": !!__props.error ? 'true' : undefined,
          "aria-describedby": describedBy.value,
          "aria-label": ariaLabel.value
        }), null, 16, _hoisted_2)), [
          [_vModelDynamic, rawValue.value]
        ])
      : (_openBlock(), _createElementBlock("div", {
          key: 2,
          ref_key: "datepickerWrapper",
          ref: datepickerWrapper
        }, [
          _createVNode(_unref(Datepicker), _mergeProps(_unref(attrs), {
            format: dateFormat.value,
            modelValue: model.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model).value = $event)),
            clearable: false,
            "month-picker": props.monthPicker,
            "auto-apply": props.autoApply,
            utc: true,
            class: {'is-invalid': !!__props.error},
            "enable-time-picker": props.enableTimePicker,
            "enable-minutes": props.enableMinutes,
            "enable-seconds": props.enableSeconds,
            "input-class-name": !!__props.error ? 'form-control is-invalid' : 'form-control',
            ui: {input: !!__props.error ? 'form-control is-invalid' : 'form-control'},
            "max-date": props.maxDate,
            "min-date": props.minDate,
            "auto-position": props.autoPosition,
            disabled: props.disabled,
            required: props.required
          }), null, 16, ["format", "modelValue", "month-picker", "auto-apply", "class", "enable-time-picker", "enable-minutes", "enable-seconds", "input-class-name", "ui", "max-date", "min-date", "auto-position", "disabled", "required"])
        ], 512)),
    (__props.error)
      ? (_openBlock(), _createElementBlock("span", {
          key: 3,
          id: __props.errorId,
          class: "invalid-feedback",
          role: "alert"
        }, _toDisplayString(__props.error), 9, _hoisted_3))
      : _createCommentVNode("", true)
  ], 64))
}
}

})